import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import CompanyAdminSidebar from '@/components/CompanyAdminSidebar';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { MapPin, Plus, Trash2, Edit2, Save, Info, X, Crosshair, Search } from 'lucide-react';
import { MapContainer, TileLayer, Polygon, useMapEvents, Marker, useMap, Circle } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Fix Leaflet marker icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const customIcon = new L.Icon({
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

// Red marker for current location
const currentLocationIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

const MapClickHandler = ({ onMapClick, isDrawing }) => {
  useMapEvents({
    click: (e) => {
      if (isDrawing) {
        onMapClick(e.latlng);
      }
    },
  });
  return null;
};

const FitBounds = ({ coordinates }) => {
  const map = useMap();
  
  useEffect(() => {
    if (coordinates && coordinates.length > 0) {
      const bounds = L.latLngBounds(coordinates.map(coord => [coord[1], coord[0]]));
      map.fitBounds(bounds, { padding: [50, 50] });
    }
  }, [coordinates, map]);
  
  return null;
};

// Component to fly to a location
const FlyToLocation = ({ center, zoom }) => {
  const map = useMap();
  
  useEffect(() => {
    if (center) {
      map.flyTo(center, zoom || 18, { duration: 1.5 });
    }
  }, [center, zoom, map]);
  
  return null;
};

// Component to center map on coordinates when editing
const CenterOnEdit = ({ coordinates, isEditing }) => {
  const map = useMap();
  
  useEffect(() => {
    if (isEditing && coordinates && coordinates.length > 0) {
      // Calculate center of polygon
      const lats = coordinates.map(c => c[0]);
      const lngs = coordinates.map(c => c[1]);
      const centerLat = (Math.min(...lats) + Math.max(...lats)) / 2;
      const centerLng = (Math.min(...lngs) + Math.max(...lngs)) / 2;
      
      // Calculate bounds and fit
      const bounds = L.latLngBounds(coordinates);
      map.fitBounds(bounds, { padding: [50, 50], maxZoom: 18 });
    }
  }, [isEditing, coordinates, map]);
  
  return null;
};

const OfficeManagement = () => {
  const [offices, setOffices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedOffice, setSelectedOffice] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const [formData, setFormData] = useState({ name: '', address: '' });
  const [polygonPoints, setPolygonPoints] = useState([]);
  const [isDrawing, setIsDrawing] = useState(false);
  
  // New state for location features
  const [currentLocation, setCurrentLocation] = useState(null);
  const [flyToCenter, setFlyToCenter] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [isLocating, setIsLocating] = useState(false);

  const fetchOffices = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/offices`);
      setOffices(response.data);
    } catch (error) {
      console.error('Failed to fetch offices:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOffices();
  }, [fetchOffices]);

  const resetForm = () => {
    setFormData({ name: '', address: '' });
    setPolygonPoints([]);
    setIsDrawing(false);
    setIsEditing(false);
    setSelectedOffice(null);
    setCurrentLocation(null);
    setFlyToCenter(null);
  };

  const openDialog = (office = null) => {
    if (office) {
      setIsEditing(true);
      setSelectedOffice(office);
      setFormData({ name: office.name, address: office.address || '' });
      // Convert [lng, lat] to [lat, lng] for Leaflet
      const points = office.coordinates.map(coord => [coord[1], coord[0]]);
      setPolygonPoints(points);
      // Don't get current location when editing - let map center on polygon
      setCurrentLocation(null);
      setFlyToCenter(null);
    } else {
      resetForm();
      // Start by getting current location for new office
      findMyLocation();
    }
    setDialogOpen(true);
  };

  const handleMapClick = (latlng) => {
    setPolygonPoints(prev => [...prev, [latlng.lat, latlng.lng]]);
  };

  // Find user's current location
  const findMyLocation = () => {
    setIsLocating(true);
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const loc = [position.coords.latitude, position.coords.longitude];
          setCurrentLocation(loc);
          setFlyToCenter(loc);
          setIsLocating(false);
          toast.success('Location found!');
        },
        (error) => {
          console.error('Geolocation error:', error);
          toast.error('Could not get your location. Please enable location services.');
          setIsLocating(false);
        },
        { enableHighAccuracy: true, timeout: 10000 }
      );
    } else {
      toast.error('Geolocation is not supported by your browser.');
      setIsLocating(false);
    }
  };

  // Search for address using Nominatim (free geocoding)
  const searchAddress = async () => {
    if (!formData.address.trim()) {
      toast.error('Please enter an address to search');
      return;
    }
    
    setIsSearching(true);
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(formData.address)}&limit=1`,
        { headers: { 'User-Agent': 'ClockInPro/1.0' } }
      );
      const data = await response.json();
      
      if (data && data.length > 0) {
        const loc = [parseFloat(data[0].lat), parseFloat(data[0].lon)];
        setFlyToCenter(loc);
        toast.success(`Found: ${data[0].display_name.substring(0, 50)}...`);
      } else {
        toast.error('Address not found. Try a different search term.');
      }
    } catch (error) {
      console.error('Geocoding error:', error);
      toast.error('Failed to search address');
    } finally {
      setIsSearching(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    
    if (polygonPoints.length < 3) {
      toast.error('Please draw at least 3 points to create a geofence');
      return;
    }

    setIsSubmitting(true);
    try {
      // Convert [lat, lng] to [lng, lat] for storage (GeoJSON format)
      const coordinates = polygonPoints.map(point => [point[1], point[0]]);
      // Close the polygon if not already closed
      if (coordinates.length > 0 && 
          (coordinates[0][0] !== coordinates[coordinates.length - 1][0] || 
           coordinates[0][1] !== coordinates[coordinates.length - 1][1])) {
        coordinates.push([...coordinates[0]]);
      }

      const payload = { ...formData, coordinates };

      if (isEditing && selectedOffice) {
        const response = await axios.put(`${API}/offices/${selectedOffice.id}`, payload);
        setOffices(prev => prev.map(o => o.id === selectedOffice.id ? response.data : o));
        toast.success('Office updated successfully!');
      } else {
        const response = await axios.post(`${API}/offices`, payload);
        setOffices(prev => [...prev, response.data]);
        toast.success('Office created successfully!');
      }
      
      setDialogOpen(false);
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save office');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedOffice) return;
    setIsSubmitting(true);
    try {
      await axios.delete(`${API}/offices/${selectedOffice.id}`);
      setOffices(prev => prev.filter(o => o.id !== selectedOffice.id));
      setDeleteDialogOpen(false);
      setSelectedOffice(null);
      toast.success('Office deleted successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete office');
    } finally {
      setIsSubmitting(false);
    }
  };

  const defaultCenter = [40.7128, -74.0060];

  return (
    <div className="flex flex-col md:flex-row min-h-screen bg-slate-50 w-full overflow-x-hidden">
      <CompanyAdminSidebar />
      
      <main className="flex-1 min-w-0 p-3 sm:p-4 md:p-8 pb-20 md:pb-8 overflow-x-hidden w-full">
        <div className="max-w-6xl mx-auto space-y-4 sm:space-y-6">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
            <div>
              <h1 className="text-xl sm:text-2xl md:text-3xl font-bold font-['Manrope'] tracking-tight">Office Locations</h1>
              <p className="text-sm sm:text-base text-slate-500">Manage office geofences for clock-in verification</p>
            </div>
            <Button 
              className="gap-2 bg-[hsl(243,65%,20%)] hover:bg-[hsl(243,65%,25%)] self-start sm:self-auto"
              onClick={() => openDialog()}
              data-testid="add-office-btn"
            >
              <Plus className="w-4 h-4" />
              Add Office
            </Button>
          </div>

          {/* Offices List */}
          <Card className="border-0 shadow-lg">
            <CardHeader className="border-b">
              <CardTitle className="font-['Manrope'] flex items-center gap-2">
                <MapPin className="w-5 h-5" />
                Offices ({offices.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {loading ? (
                <div className="flex justify-center py-12">
                  <div className="spinner" />
                </div>
              ) : offices.length === 0 ? (
                <div className="empty-state py-12">
                  <MapPin className="empty-state-icon" />
                  <p className="text-slate-500">No office locations yet</p>
                  <p className="text-sm text-slate-400">Add your first office to enable clock-in</p>
                </div>
              ) : (
                <ScrollArea className="h-[500px]">
                  <div className="grid md:grid-cols-2 gap-4 p-6">
                    {offices.map((office) => (
                      <Card key={office.id} className="border shadow-sm" data-testid={`office-card-${office.id}`}>
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between mb-3">
                            <div>
                              <h3 className="font-semibold font-['Manrope']">{office.name}</h3>
                              {office.address && (
                                <p className="text-sm text-slate-500">{office.address}</p>
                              )}
                              <p className="text-xs text-slate-400 mt-1">
                                {office.coordinates?.length || 0} boundary points
                              </p>
                            </div>
                            <div className="flex gap-1">
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => openDialog(office)}
                                data-testid={`edit-office-${office.id}`}
                              >
                                <Edit2 className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-slate-400 hover:text-[hsl(var(--destructive))]"
                                onClick={() => {
                                  setSelectedOffice(office);
                                  setDeleteDialogOpen(true);
                                }}
                                data-testid={`delete-office-${office.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                          <div className="h-40 rounded-lg overflow-hidden">
                            {!dialogOpen && (
                              <MapContainer
                                key={`preview-${office.id}`}
                                center={office.coordinates[0] ? [office.coordinates[0][1], office.coordinates[0][0]] : defaultCenter}
                                zoom={17}
                                style={{ height: '100%', width: '100%' }}
                                dragging={false}
                                zoomControl={false}
                                scrollWheelZoom={false}
                              >
                                <TileLayer
                                  url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                                  attribution='&copy; Esri'
                                />
                                <Polygon 
                                  positions={office.coordinates.map(coord => [coord[1], coord[0]])}
                                  pathOptions={{
                                    color: '#00FF00',
                                    fillColor: '#00FF00',
                                    fillOpacity: 0.3,
                                    weight: 3
                                  }}
                                />
                                <FitBounds coordinates={office.coordinates} />
                              </MapContainer>
                            )}
                            {dialogOpen && (
                              <div className="h-full w-full bg-slate-200 flex items-center justify-center">
                                <MapPin className="w-8 h-8 text-slate-400" />
                              </div>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </ScrollArea>
              )}
            </CardContent>
          </Card>
        </div>
      </main>

      {/* Add/Edit Office Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => { if (!open) resetForm(); setDialogOpen(open); }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-['Manrope']">
              {isEditing ? 'Edit Office' : 'Add New Office'}
            </DialogTitle>
            <DialogDescription>
              {isEditing ? 'Update office details and geofence boundary' : 'Create a new office location with its geofence boundary'}
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSave} className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="office-name">Office Name</Label>
                <Input
                  id="office-name"
                  placeholder="Main Office"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  data-testid="office-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="office-address">Address</Label>
                <div className="flex gap-2">
                  <Input
                    id="office-address"
                    placeholder="123 Main St, City, Country"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    data-testid="office-address-input"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={searchAddress}
                    disabled={isSearching}
                    data-testid="search-address-btn"
                  >
                    {isSearching ? <div className="spinner w-4 h-4" /> : <Search className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
            </div>

            {/* Location Controls */}
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={findMyLocation}
                disabled={isLocating}
                className="gap-2"
                data-testid="find-location-btn"
              >
                {isLocating ? <div className="spinner w-4 h-4" /> : <Crosshair className="w-4 h-4" />}
                Find My Location
              </Button>
              
              {!isDrawing ? (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => { setPolygonPoints([]); setIsDrawing(true); }}
                  className="gap-2"
                  data-testid="start-drawing-btn"
                >
                  <MapPin className="w-4 h-4" />
                  {polygonPoints.length > 0 ? 'Redraw Boundary' : 'Start Drawing'}
                </Button>
              ) : (
                <>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setPolygonPoints(prev => prev.slice(0, -1))}
                    disabled={polygonPoints.length === 0}
                  >
                    Undo Last Point
                  </Button>
                  <Button
                    type="button"
                    variant="default"
                    onClick={() => setIsDrawing(false)}
                    className="gap-2 bg-[hsl(var(--success))] hover:bg-[hsl(var(--success))]/90"
                  >
                    Done Drawing ({polygonPoints.length} points)
                  </Button>
                </>
              )}
            </div>

            {/* Map */}
            <div className="space-y-2">
              <Label>Geofence Boundary (click on map to add points)</Label>
              <div className="h-[350px] rounded-lg overflow-hidden border">
                <MapContainer
                  key={isEditing ? `edit-${selectedOffice?.id}` : 'new'}
                  center={polygonPoints[0] || currentLocation || defaultCenter}
                  zoom={18}
                  style={{ height: '100%', width: '100%' }}
                >
                  {/* Satellite imagery */}
                  <TileLayer
                    url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                    attribution='&copy; Esri'
                  />
                  {/* Street labels overlay */}
                  <TileLayer
                    url="https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}"
                  />
                  
                  <MapClickHandler onMapClick={handleMapClick} isDrawing={isDrawing} />
                  
                  {/* Center on existing polygon when editing */}
                  <CenterOnEdit coordinates={polygonPoints} isEditing={isEditing} />
                  
                  {/* Fly to location when searching */}
                  {flyToCenter && <FlyToLocation center={flyToCenter} zoom={18} />}
                  
                  {/* Current location marker */}
                  {currentLocation && (
                    <>
                      <Marker position={currentLocation} icon={currentLocationIcon} />
                      <Circle 
                        center={currentLocation} 
                        radius={10} 
                        pathOptions={{ color: 'red', fillColor: 'red', fillOpacity: 0.3 }}
                      />
                    </>
                  )}
                  
                  {/* Polygon drawing */}
                  {polygonPoints.length > 0 && (
                    <>
                      <Polygon 
                        positions={polygonPoints}
                        pathOptions={{
                          color: '#00FF00',
                          fillColor: '#00FF00',
                          fillOpacity: 0.3,
                          weight: 3
                        }}
                      />
                      {isDrawing && polygonPoints.map((point, index) => (
                        <Marker key={index} position={point} icon={customIcon} />
                      ))}
                    </>
                  )}
                </MapContainer>
              </div>
              
              <div className="flex items-center justify-between text-sm">
                <p className="text-slate-500">
                  {isDrawing 
                    ? `Click on the map to add boundary points. Points: ${polygonPoints.length}` 
                    : polygonPoints.length > 0 
                      ? `Geofence has ${polygonPoints.length} points. Click "Redraw" to modify.`
                      : 'Click "Start Drawing" then click on the map to draw the boundary'
                  }
                </p>
                {currentLocation && (
                  <p className="text-xs text-slate-400">
                    Your location: {currentLocation[0].toFixed(6)}, {currentLocation[1].toFixed(6)}
                  </p>
                )}
              </div>
            </div>

            {/* Instructions */}
            <Card className="bg-[hsl(var(--info-bg))] border-0">
              <CardContent className="pt-4 pb-4">
                <div className="flex gap-3 text-sm">
                  <Info className="w-4 h-4 text-[hsl(var(--info))] flex-shrink-0 mt-0.5" />
                  <div className="text-slate-600 space-y-1">
                    <p><strong>Tips for accurate geofencing:</strong></p>
                    <ul className="list-disc list-inside space-y-0.5 text-xs">
                      <li>Use "Find My Location" to center the map on your current position</li>
                      <li>Or enter an address and click the search button to navigate there</li>
                      <li>Draw the boundary around the office building/area</li>
                      <li>Make the geofence slightly larger to account for GPS accuracy (~10-20 meters)</li>
                      <li>Draw at least 3 points to create a valid polygon</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => { resetForm(); setDialogOpen(false); }}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                className="bg-[hsl(243,65%,20%)] hover:bg-[hsl(243,65%,25%)]"
                disabled={isSubmitting || polygonPoints.length < 3}
                data-testid="save-office-btn"
              >
                {isSubmitting ? <div className="spinner" /> : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    {isEditing ? 'Update Office' : 'Create Office'}
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Office</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{selectedOffice?.name}</strong>? 
              Employees will no longer be able to clock in at this location.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-[hsl(var(--destructive))] hover:bg-[hsl(var(--destructive))]/90"
              onClick={handleDelete}
              disabled={isSubmitting}
              data-testid="confirm-delete-office"
            >
              {isSubmitting ? <div className="spinner" /> : 'Delete Office'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default OfficeManagement;
